import { Injectable } from '@angular/core';
import { TokenService } from './token.service';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { serverLocation } from 'src/app/globals';
import { buffer, catchError, debounceTime, delay, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { User } from '../../user/classes';
import { Setting } from 'src/app/classes';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  static globalSettingNames = {
    'BACKGROUND_PATTERN': 'background-pattern',
    'NEXTCLOUD_USERNAME': 'nextcloud-username',
    'NEXTCLOUD_TOKEN': 'nextcloud-token',
    'NEXTCLOUD_REFRESH_TOKEN': 'nextcloud-refresh-token',
    'SHOW_NEWSFEED': 'show-newsfeed'
  };

  private _settings: { [key: number]: ReplaySubject<Setting> } = {};
  private _currentSettingsLoaded = new BehaviorSubject(false);

  private _userLoaded = new BehaviorSubject(false);
  userLoaded$ = this._userLoaded.asObservable();

  private _user: ReplaySubject<User> = new ReplaySubject(1);
  user$ = this._user.asObservable();
  userId$ = this.user$.pipe(map(x => x.id));
  userImage$ = this.user$.pipe(map(x => x && x.profilePictureMediaType && x.profilePicture ? `data:${x.profilePictureMediaType};base64,${x.profilePicture}` : null));

  private _rightsMap: { [key: string]: ReplaySubject<boolean> } = {};
  private _requestedRightsBuffer = new Subject<string>();

  private _bgColor = new BehaviorSubject('#dadada');
  bgColor$ = this._bgColor.asObservable();

  private _autoLogoutSeconds = new ReplaySubject<number>(1);
  autoLogoutSeconds$ = this._autoLogoutSeconds.asObservable();

  autoLogoutDisabled: boolean = false;

  private _autoLogoutSub: Subscription = null;

  loggedIn$ = combineLatest([this.userLoaded$.pipe(distinctUntilChanged()), this._user.pipe(distinctUntilChanged())]).pipe(map(([loaded, user]) => {
    return loaded && user !== null ? true : false;
  }));

  private _ready = new BehaviorSubject(true);
  ready$ = this._ready.asObservable();

  private _logoffSubject = new Subject();
  logoff$ = this._logoffSubject.asObservable();

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService,
    private _router: Router
  ) {
    this._requestedRightsBuffer
      .pipe(
        buffer(this._requestedRightsBuffer.pipe(debounceTime(1000))),
        switchMap((rules: string[]) => combineLatest([of(rules), this._hasRights(rules)]))
      )
      .subscribe(([rules, result]) => {
        for (let rule of rules) this._rightsMap[rule].next(result.find(x => x.rule === rule)?.hasRight ? true : false);
      });
    this.setUp();
  }

  confirmPasswordReset(object) {
    return this._httpClient.post(`${serverLocation}/api/account/confirm-password-reset`, object);
  }

  createOwnSetting(setting: Setting): Observable<Setting> {
    return this._httpClient.post(`${serverLocation}/api/setting/current`, setting, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Setting>;
  }

  deleteOwnSetting(setting: Setting): Observable<Object> {
    if (!setting) return throwError('no setting passed');
    let subject = new Subject();
    this._deleteOwnSetting(setting.stgid).pipe(
      tap(() => {
        if (this._settings[setting.stgid]) {
          this._settings[setting.stgid].complete();
          delete this._settings[setting.stgid];
        }
      })
    ).subscribe(subject);
    return subject.asObservable();
  }

  getSettingByName(settingName: string, soft: boolean = true): Observable<Setting> {
    let callback = () => {
      return this._loadSettingByName(settingName).pipe(tap((result: Setting) => {
        if (!result) return;
        if (!this._settings[result.stgid]) this._settings[result.stgid] = new ReplaySubject(1);
        this._settings[result.stgid].next(result);
      }))
    };
    if (!soft || !this._currentSettingsLoaded.value) return callback();
    return combineLatest(Object.values(this._settings)).pipe(switchMap((settings: Setting[]) => {
      let setting = settings.find(x => x.stgname === settingName);
      return setting ? of(setting) : callback();
    }))
  }

  hasRight(rule: string, soft: boolean = true): Observable<boolean> {
    if (!soft || !this._rightsMap[rule]) {
      if (!this._rightsMap[rule]) this._rightsMap[rule] = new ReplaySubject(1);
      this._requestedRightsBuffer.next(rule);
    }
    return this._rightsMap[rule].asObservable();
  }

  loadCurrentSettings(soft: boolean = true): Observable<Setting[]> {
    let subject = new ReplaySubject<Setting[]>(1);
    if (!soft || !this._currentSettingsLoaded.value) {
      this._loadCurrentSettings().subscribe((result: Setting[]) => {
        for (let setting of result) {
          if (!this._settings[setting.stgid]) this._settings[setting.stgid] = new ReplaySubject(1);
          this._settings[setting.stgid].next(setting);
          switch (setting.stgname) {
            case SessionService.globalSettingNames.BACKGROUND_PATTERN:
              this._bgColor.next(setting.stgvalue);
              break;
          }
        }
        subject.next(result);
        this._currentSettingsLoaded.next(true);
      });
    } else combineLatest(Object.values(this._settings)).subscribe((result) => subject.next(result));
    return subject.asObservable();
  }

  login(username: string, password: string): Observable<User> {
    this
      ._httpClient
      .post(`${serverLocation}/token`, `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this._user.error(error);
          return throwError(error);
        }),
        tap((result: any) => {
          this._tokenService.token = result.access_token;
          this._tokenService.tokenExpiration = (moment().unix() + result.expires_in).toFixed(0);
        }),
        switchMap(_ => this._loadCurrentUser())
      )
      .toPromise();
    return this._user.asObservable();
  }

  logout(queryParams: any = null) {
    this._logoffSubject.next();
    this._tokenService.destroyLocalSession();
    this._router.navigate(["/login"], {
      queryParams: queryParams
    }).then(() => window.location.reload());
  }

  resetAutoLoginTimer() {
    if (this._autoLogoutSub) this._autoLogoutSub.unsubscribe();
    this._autoLogoutSeconds.next(1200);
    this._autoLogoutSub = this._autoLogoutSeconds.pipe(delay(1000)).subscribe((value: number) => {
      if (this.autoLogoutDisabled) this._autoLogoutSeconds.next(1200);
      else {
        if (value <= 0) this.logout();
        else {
          this._autoLogoutSeconds.next(value - 1);
        }
      }
    });
  }

  setBackgroundColor(color: string) {
    this._bgColor.next(color);
    this.updateOwnSettingByName(SessionService.globalSettingNames.BACKGROUND_PATTERN, color).toPromise();
  }

  setReadyStatus(ready: boolean) {
    this._ready.next(ready);
  }

  setUp() {
    this.resetAutoLoginTimer();
  }

  startPasswordReset(userName: string, email: string) {
    return this._httpClient.post(`${serverLocation}/api/account/reset-password`, {
      email: email,
      userName: userName
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  tryLogin(): Observable<User> {
    return this._checkLogin() ? this._loadCurrentUser() : throwError('no valid credentials, please login');
  }

  updateUser = (user: User) => this._user.next(user);

  updateOwnSettingByName(name: string, value: string): Observable<Setting> {
    return this.getSettingByName(name, true).pipe(
      map((setting: Setting) => {
        if (setting) return setting;
        setting = new Setting();
        setting.stgname = name;
        return setting;
      }),
      tap(x => x.stgvalue = value),
      switchMap((setting: Setting) => setting.stgid ? this.updateOwnSetting(setting) : this.createOwnSetting(setting))
    );
  }

  updateOwnSetting(setting: Setting): Observable<Setting> {
    return this._httpClient.put(`${serverLocation}/api/setting/current`, setting, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Setting>;
  }

  private _checkLogin(): boolean {
    let token = this._tokenService.token;
    let expiration = this._tokenService.tokenExpiration;
    if (typeof token === 'string' && typeof expiration === 'number') return moment().unix() < moment(expiration * 1000).unix();
    return false;
  }

  private _deleteOwnSetting(settingId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/setting/current/${settingId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  private _hasRights(rules: string[]): Observable<{ rule: string, hasRight: boolean }[]> {
    return this._httpClient.post(`${serverLocation}/api/role-right/current/has-rights`, rules, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<{ rule: string, hasRight: boolean }[]>;
  }

  private _loadCurrentSettings(): Observable<Setting[]> {
    return this._httpClient.get(`${serverLocation}/api/setting/current`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Setting[]>;
  }

  private _loadCurrentUser(soft: boolean = true): Observable<User> {
    if (!soft || !this._userLoaded.value) {
      (this._httpClient.get(`${serverLocation}/api/user`, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
      }) as Observable<User>).subscribe({
        next: (result) => {
          if (result.customerOnly) {
            this._tokenService.destroyLocalSession();
            window.location.href = "https://ams.campus-netz.services/customer-client?internal-redirect=true";
            return;
          }
          this._user.next(result);
          this._userLoaded.next(true);
          this.loadCurrentSettings(true);
        },
        error: (error) => this._user.error(error)
      })
    }
    return this._user.asObservable();
  }

  private _loadSettingByName(settingName: string): Observable<Setting> {
    return this._httpClient.get(`${serverLocation}/api/setting/current/${settingName}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Setting>;
  }
}
